"use client";
import type { UseChatHelpers } from "@ai-sdk/react";
import equal from "fast-deep-equal";
import { motion } from "framer-motion";
import { memo, useCallback, useState } from "react";
import type { Vote } from "@/lib/db/schema";
import type { ChatMessage } from "@/lib/types";
import { cn, sanitizeText } from "@/lib/utils";
import {
  EventLogs,
  TokenCard,
  TransactionDetails,
  TransactionList,
  WalletOverview,
} from "./blockchain";
import { CollapseProvider } from "./blockchain/collapse-context";
import { FoldAllButton } from "./blockchain/fold-all-button";
import type { QuickActionHandler } from "./blockchain/types";
import { MessageContent } from "./elements/message";
import { Response } from "./elements/response";
import { SparklesIcon } from "./icons";
import { MessageActions } from "./message-actions";
import { MessageEditor } from "./message-editor";
import { MessageReasoning } from "./message-reasoning";
import { PreviewAttachment } from "./preview-attachment";

/**
 * Generate a simple stable hash from a string for use in React keys.
 * Uses djb2 algorithm - fast and produces good distribution.
 */
function simpleHash(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  // Convert to positive number and take last 8 hex digits
  return (hash >>> 0).toString(16).padStart(8, "0");
}

/**
 * Generate a stable unique key for a message part.
 * Keys are based on content fingerprints, not array indices,
 * ensuring stability when deduplication changes array positions.
 */
function getStablePartKey(
  part: ChatMessage["parts"][number],
  messageId: string,
  typeOccurrence: number
): string {
  const { type } = part;

  // Tool parts have stable toolCallId - use that
  if ("toolCallId" in part && part.toolCallId) {
    return part.toolCallId;
  }

  // For text parts, use a hash of the content prefix for stability
  // This handles cases where text is streamed incrementally
  if (type === "text" && "text" in part) {
    const textContent = (part.text as string) || "";
    // Use first 50 chars for fingerprint - enough to differentiate parts
    // but stable even as more content streams in.
    // If content is too short (still streaming in), use just occurrence
    // to avoid key changes as initial content arrives
    if (textContent.length < 10) {
      return `${type}-${messageId}-${typeOccurrence}`;
    }
    const fingerprint = textContent.slice(0, 50);
    return `${type}-${messageId}-${typeOccurrence}-${simpleHash(fingerprint)}`;
  }

  // For reasoning parts, similar approach
  if (type === "reasoning" && "text" in part) {
    const reasoningContent = (part.text as string) || "";
    if (reasoningContent.length < 10) {
      return `${type}-${messageId}-${typeOccurrence}`;
    }
    const fingerprint = reasoningContent.slice(0, 50);
    return `${type}-${messageId}-${typeOccurrence}-${simpleHash(fingerprint)}`;
  }

  // Fallback for any other part types - use type + occurrence
  return `${type}-${messageId}-${typeOccurrence}`;
}

/**
 * Part with its stable key attached
 */
type PartWithKey = {
  part: ChatMessage["parts"][number];
  stableKey: string;
};

/**
 * Deduplicate message parts by toolCallId and attach stable keys.
 * When the SDK accidentally includes duplicate tool invocations,
 * keep only the last occurrence (most complete state).
 *
 * This function:
 * 1. Assigns stable keys to each part BEFORE deduplication
 * 2. Filters out duplicate tool parts (keeping last occurrence)
 * 3. Returns parts with their stable keys preserved
 *
 * This ensures React keys remain stable even when array positions
 * change due to deduplication.
 */
function deduplicatePartsWithStableKeys(
  parts: ChatMessage["parts"],
  messageId: string
): PartWithKey[] {
  if (!parts || parts.length === 0) {
    return [];
  }

  // First: assign stable keys to all parts based on their original position
  // Track occurrences by type for the type-occurrence component of keys
  const typeOccurrences = new Map<string, number>();
  const partsWithKeys: PartWithKey[] = parts.map((part) => {
    const occurrence = typeOccurrences.get(part.type) ?? 0;
    typeOccurrences.set(part.type, occurrence + 1);
    return {
      part,
      stableKey: getStablePartKey(part, messageId, occurrence),
    };
  });

  // Second: find which toolCallIds to keep (last occurrence)
  const toolCallLastIndex = new Map<string, number>();
  parts.forEach((part, index) => {
    if ("toolCallId" in part && part.toolCallId) {
      toolCallLastIndex.set(part.toolCallId, index);
    }
  });

  // Third: filter out earlier duplicates while preserving stable keys
  return partsWithKeys.filter((item, index) => {
    const { part } = item;
    if ("toolCallId" in part && part.toolCallId) {
      return toolCallLastIndex.get(part.toolCallId) === index;
    }
    return true;
  });
}

const PurePreviewMessage = ({
  chatId,
  message,
  vote,
  isLoading,
  setMessages,
  regenerate,
  sendMessage,
  setInput,
  isReadonly,
  requiresScrollPadding,
}: {
  chatId: string;
  message: ChatMessage;
  vote: Vote | undefined;
  isLoading: boolean;
  setMessages: UseChatHelpers<ChatMessage>["setMessages"];
  regenerate: UseChatHelpers<ChatMessage>["regenerate"];
  sendMessage: UseChatHelpers<ChatMessage>["sendMessage"];
  setInput: (value: string) => void;
  isReadonly: boolean;
  requiresScrollPadding: boolean;
}) => {
  const [mode, setMode] = useState<"view" | "edit">("view");

  const attachmentsFromMessage = message.parts.filter(
    (part) => part.type === "file"
  );

  // Quick action handler for blockchain components
  const handleQuickAction: QuickActionHandler = useCallback(
    (prompt: string, autoSend = false) => {
      if (autoSend) {
        sendMessage({
          role: "user",
          parts: [{ type: "text", text: prompt }],
        });
      } else {
        setInput(prompt);
        // Focus the input
        requestAnimationFrame(() => {
          const input = document.querySelector<HTMLTextAreaElement>(
            '[data-testid="chat-input"], textarea'
          );
          if (input) {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
          }
        });
      }
    },
    [sendMessage, setInput]
  );

  return (
    <motion.div
      animate={{ opacity: 1 }}
      className="group/message w-full"
      data-role={message.role}
      data-testid={`message-${message.role}`}
      initial={{ opacity: 0 }}
    >
      <div
        className={cn("flex w-full items-start gap-2 md:gap-3", {
          "justify-end": message.role === "user" && mode !== "edit",
          "justify-start": message.role === "assistant",
        })}
      >
        {message.role === "assistant" && (
          <div className="-mt-1 flex size-8 shrink-0 items-center justify-center rounded-full bg-background ring-1 ring-border">
            <SparklesIcon size={14} />
          </div>
        )}

        <div
          className={cn("flex flex-col", {
            "gap-2 md:gap-4": message.role === "assistant",
            "min-h-96": message.role === "assistant" && requiresScrollPadding,
            "w-full": message.role === "assistant" || mode === "edit",
            "max-w-[calc(100%-2.5rem)] sm:max-w-[min(fit-content,80%)]":
              message.role === "user" && mode !== "edit",
          })}
        >
          {attachmentsFromMessage.length > 0 && (
            <div
              className="flex flex-row justify-end gap-2"
              data-testid={"message-attachments"}
            >
              {attachmentsFromMessage.map((attachment) => (
                <PreviewAttachment
                  attachment={{
                    name: attachment.filename ?? "file",
                    contentType: attachment.mediaType,
                    url: attachment.url,
                  }}
                  key={attachment.url}
                />
              ))}
            </div>
          )}

          <CollapseProvider>
            {deduplicatePartsWithStableKeys(message.parts, message.id).map(
              ({ part, stableKey }) => {
                const { type } = part;

                if (type === "reasoning" && part.text?.trim().length > 0) {
                  return (
                    <MessageReasoning
                      isLoading={isLoading}
                      key={stableKey}
                      reasoningText={part.text}
                    />
                  );
                }

                if (type === "text") {
                  if (mode === "view") {
                    return (
                      <div key={stableKey}>
                        <MessageContent
                          className={cn({
                            "wrap-break-word [&_a]:!text-primary-foreground w-fit rounded-2xl px-3 py-2 text-right text-primary-foreground [&_a]:underline [&_a]:underline-offset-2":
                              message.role === "user",
                            "bg-transparent px-0 py-0 text-left":
                              message.role === "assistant",
                          })}
                          data-testid="message-content"
                          style={
                            message.role === "user"
                              ? { backgroundColor: "var(--primary)" }
                              : undefined
                          }
                        >
                          <Response isStreaming={isLoading}>
                            {sanitizeText(part.text)}
                          </Response>
                        </MessageContent>
                      </div>
                    );
                  }

                  if (mode === "edit") {
                    return (
                      <div
                        className="flex w-full flex-row items-start gap-3"
                        key={stableKey}
                      >
                        <div className="size-8" />
                        <div className="min-w-0 flex-1">
                          <MessageEditor
                            key={message.id}
                            message={message}
                            regenerate={regenerate}
                            setMessages={setMessages}
                            setMode={setMode}
                          />
                        </div>
                      </div>
                    );
                  }
                }

                // =================================================================
                // Blockchain Tool Handlers
                // =================================================================

                if (type === "tool-getTransactionsDetails") {
                  const { toolCallId, state, input, output } = part;
                  const safeInput = input ?? { transactions: [], chainId: "" };

                  return (
                    <div className="w-full" key={stableKey}>
                      <TransactionDetails
                        cardId={`${message.id}-${toolCallId}`}
                        input={
                          safeInput as {
                            transactions: string[];
                            chainId: string;
                          }
                        }
                        isReadonly={isReadonly}
                        onQuickAction={handleQuickAction}
                        output={transformTransactionDetailsOutput(output)}
                        state={state}
                      />
                    </div>
                  );
                }

                if (type === "tool-retrieveTransactions") {
                  const { toolCallId, state, input, output } = part;
                  const safeInput = input ?? {
                    address: "",
                    chainId: "",
                    startTime: "",
                    endTime: "",
                  };

                  return (
                    <div className="w-full" key={stableKey}>
                      <TransactionList
                        cardId={`${message.id}-${toolCallId}`}
                        input={
                          safeInput as {
                            address: string;
                            chainId: string;
                            startTime: string;
                            endTime: string;
                            numTransactions?: number;
                          }
                        }
                        isReadonly={isReadonly}
                        onQuickAction={handleQuickAction}
                        output={transformTransactionListOutput(output)}
                        state={state}
                      />
                    </div>
                  );
                }

                if (type === "tool-getWalletState") {
                  const { toolCallId, state, input, output } = part;
                  const safeInput = (input ?? {
                    walletAddresses: [],
                    chainId: "",
                  }) as { walletAddresses: string[]; chainId: string };

                  return (
                    <div className="w-full" key={stableKey}>
                      <WalletOverview
                        cardId={`${message.id}-${toolCallId}`}
                        input={safeInput}
                        isReadonly={isReadonly}
                        onQuickAction={handleQuickAction}
                        output={transformWalletStateOutput(output, safeInput)}
                        state={state}
                      />
                    </div>
                  );
                }

                if (type === "tool-getTokenStates") {
                  const { toolCallId, state, input, output } = part;
                  const safeInput = input ?? { tokens: [], chainId: "" };

                  return (
                    <div className="w-full" key={stableKey}>
                      <TokenCard
                        cardId={`${message.id}-${toolCallId}`}
                        input={
                          safeInput as { tokens: string[]; chainId: string }
                        }
                        isReadonly={isReadonly}
                        onQuickAction={handleQuickAction}
                        output={transformTokenStatesOutput(output)}
                        state={state}
                      />
                    </div>
                  );
                }

                if (type === "tool-getEventLogs") {
                  const { toolCallId, state, input, output } = part;
                  const safeInput = input ?? {
                    chainId: "",
                    fromBlock: "0",
                    toBlock: "latest",
                  };

                  return (
                    <div className="w-full" key={stableKey}>
                      <EventLogs
                        cardId={`${message.id}-${toolCallId}`}
                        input={
                          safeInput as {
                            chainId: string;
                            address?: string;
                            fromBlock: string;
                            toBlock: string;
                            topic0?: string;
                            topic1?: string;
                            topic2?: string;
                            topic3?: string;
                          }
                        }
                        isReadonly={isReadonly}
                        onQuickAction={handleQuickAction}
                        output={transformEventLogsOutput(output)}
                        state={state}
                      />
                    </div>
                  );
                }

                return null;
              }
            )}

            {/* Fold All button - only visible when there are 2+ collapsible cards */}
            {message.role === "assistant" && <FoldAllButton />}
          </CollapseProvider>

          {!isReadonly && (
            <MessageActions
              chatId={chatId}
              isLoading={isLoading}
              key={`action-${message.id}`}
              message={message}
              setMode={setMode}
              vote={vote}
            />
          )}
        </div>
      </div>
    </motion.div>
  );
};

// =============================================================================
// Data Transformation Functions
// =============================================================================

/**
 * Type guard to check if output is an AIToolError
 */
function isAIToolError(output: unknown): output is {
  name: string;
  userMessage: string;
  suggestions?: string[];
  context?: Record<string, unknown>;
} {
  if (!output || typeof output !== "object") {
    return false;
  }
  const obj = output as Record<string, unknown>;
  return (
    (obj.name === "AIToolError" || obj.name === "BlockScoutRateLimitError") &&
    typeof obj.userMessage === "string"
  );
}

/**
 * Transform raw API output to TransactionDetails component format
 */
function transformTransactionDetailsOutput(output: unknown) {
  if (!output) {
    return;
  }

  // Check for legacy error format
  if (typeof output === "object" && "error" in (output as object)) {
    return output as { error: string };
  }

  // Check for AIToolError format (from tool execution errors)
  if (isAIToolError(output)) {
    return {
      error: output.userMessage,
      suggestions: output.suggestions,
      context: output.context,
    };
  }

  // Transform the raw API response to our component format
  // Ensure output is an array before mapping
  if (!Array.isArray(output)) {
    console.warn(
      "transformTransactionDetailsOutput: expected array, got:",
      typeof output,
      output
    );
    return;
  }

  const rawOutput = output as Array<{ tx: unknown; logs: unknown }>;

  return rawOutput.map((item) => {
    const tx = item.tx as Record<string, unknown>;
    const logs = (item.logs as Record<string, unknown>[]) ?? [];

    return {
      hash: String(tx.hash ?? ""),
      chainId: "",
      from: String((tx.from as Record<string, unknown>)?.hash ?? tx.from ?? ""),
      to: tx.to
        ? String((tx.to as Record<string, unknown>)?.hash ?? tx.to)
        : null,
      value: String(tx.value ?? "0"),
      gasUsed: String(tx.gas_used ?? tx.gasUsed ?? "0"),
      gasPrice: String(tx.gas_price ?? tx.gasPrice ?? "0"),
      timestamp: String(tx.timestamp ?? tx.block_timestamp ?? ""),
      status: tx.status === "ok" || tx.status === "1" ? "success" : "failed",
      method: tx.method ? String(tx.method) : undefined,
      blockNumber: String(tx.block ?? tx.blockNumber ?? "0"),
      nonce: Number(tx.nonce ?? 0),
      contractCreated: tx.created_contract
        ? String(
            (tx.created_contract as Record<string, unknown>)?.hash ??
              tx.created_contract
          )
        : undefined,
      input: String(tx.input ?? tx.raw_input ?? "0x"),
      logs: logs.map((log) => ({
        name: log.decoded
          ? String((log.decoded as Record<string, unknown>).method_call ?? "")
          : "Unknown",
        address: String(
          (log.address as Record<string, unknown>)?.hash ?? log.address ?? ""
        ),
        topics: (log.topics as string[]) ?? [],
        data: String(log.data ?? ""),
        decoded: log.decoded
          ? {
              eventName: String(
                (log.decoded as Record<string, unknown>).method_call ?? ""
              ),
              args: Object.fromEntries(
                (
                  (log.decoded as Record<string, unknown>).parameters as Array<{
                    name: string;
                    value: unknown;
                  }>
                )?.map((p) => [p.name, String(p.value)]) ?? []
              ),
            }
          : undefined,
      })),
      tokenTransfers: (tx.token_transfers as Record<string, unknown>[])?.map(
        (transfer) => ({
          token: {
            address: String(
              (transfer.token as Record<string, unknown>)?.address ?? ""
            ),
            symbol: String(
              (transfer.token as Record<string, unknown>)?.symbol ?? "?"
            ),
            name: String(
              (transfer.token as Record<string, unknown>)?.name ?? "Unknown"
            ),
            decimals: Number(
              (transfer.token as Record<string, unknown>)?.decimals ?? 18
            ),
            type: String(
              (transfer.token as Record<string, unknown>)?.type ?? "ERC20"
            ) as "ERC20" | "ERC721" | "ERC1155" | "NATIVE" | "UNKNOWN",
          },
          from: String(
            (transfer.from as Record<string, unknown>)?.hash ??
              transfer.from ??
              ""
          ),
          to: String(
            (transfer.to as Record<string, unknown>)?.hash ?? transfer.to ?? ""
          ),
          value: String(
            (transfer.total as Record<string, unknown>)?.value ??
              transfer.value ??
              "0"
          ),
          tokenId: transfer.token_id ? String(transfer.token_id) : undefined,
        })
      ),
    };
  }) as import("./blockchain/types").TransactionDetails[];
}

/**
 * Transform raw API output to TransactionList component format
 */
function transformTransactionListOutput(output: unknown) {
  if (!output) {
    return;
  }

  // Check for legacy error format
  if (typeof output === "object" && "error" in (output as object)) {
    return output as { error: string };
  }

  // Check for AIToolError format
  if (isAIToolError(output)) {
    return {
      error: output.userMessage,
      suggestions: output.suggestions,
      context: output.context,
    };
  }

  const rawOutput = output as Record<string, unknown>;
  const transactions =
    (rawOutput.transactions as Record<string, unknown>[]) ?? [];

  return {
    transactions: transactions.map((tx) => ({
      hash: String(tx.hash ?? ""),
      chainId: String(tx.chainId ?? ""),
      from: String(tx.from ?? ""),
      to: String(tx.to ?? ""),
      value: String(tx.value ?? "0"),
      gasUsed: String(tx.gasUsed ?? "0"),
      gasPrice: String(tx.gasPrice ?? "0"),
      timestamp: tx.timeStamp
        ? new Date(Number(tx.timeStamp) * 1000).toISOString()
        : "",
      status: tx.isError === "0" ? "success" : "failed",
      method: tx.methodId ? String(tx.methodId) : undefined,
      blockNumber: String(tx.blockNumber ?? "0"),
    })) as import("./blockchain/types").TransactionSummary[],
    numTransactionsFound: rawOutput.numTransactionsFound
      ? Number(rawOutput.numTransactionsFound)
      : undefined,
    timeRange: rawOutput.timeRange as
      | { start: string; end: string }
      | undefined,
    message: rawOutput.message ? String(rawOutput.message) : undefined,
  };
}

/**
 * Transform raw API output to WalletOverview component format
 */
function transformWalletStateOutput(
  output: unknown,
  input: { walletAddresses: string[]; chainId: string }
) {
  if (!output) {
    return;
  }

  // Check for legacy error format
  if (typeof output === "object" && "error" in (output as object)) {
    return output as { error: string };
  }

  // Check for AIToolError format
  if (isAIToolError(output)) {
    return {
      error: output.userMessage,
      suggestions: output.suggestions,
      context: output.context,
    };
  }

  // Ensure output is an array before mapping
  if (!Array.isArray(output)) {
    console.warn(
      "transformWalletStateOutput: expected array, got:",
      typeof output,
      output
    );
    return;
  }

  const rawOutput = output as Array<{
    tokens: { tokens?: Record<string, unknown>[] };
    transfers: { transfers: Record<string, unknown>[] };
  }>;

  return rawOutput.map((wallet, idx) => ({
    address: input.walletAddresses[idx] ?? "",
    chainId: input.chainId,
    tokens: (wallet.tokens?.tokens ?? []).map((tokenData) => {
      const token = tokenData.token as Record<string, unknown>;
      return {
        token: {
          address: String(token?.address ?? ""),
          symbol: String(token?.symbol ?? "?"),
          name: String(token?.name ?? "Unknown"),
          decimals: Number(token?.decimals ?? 18),
          type: String(token?.type ?? "ERC20") as
            | "ERC20"
            | "ERC721"
            | "ERC1155"
            | "NATIVE"
            | "UNKNOWN",
          iconUrl: token?.icon_url ? String(token.icon_url) : undefined,
        },
        balance: String(tokenData.value ?? "0"),
        usdValue: undefined,
      };
    }) as import("./blockchain/types").TokenBalance[],
    recentTransfers: (wallet.transfers?.transfers ?? []).map((transfer) => {
      const token = transfer.token as Record<string, unknown>;
      return {
        token: {
          address: String(token?.address ?? ""),
          symbol: String(token?.symbol ?? "?"),
          name: String(token?.name ?? "Unknown"),
          decimals: Number(token?.decimals ?? 18),
          type: String(token?.type ?? "ERC20") as
            | "ERC20"
            | "ERC721"
            | "ERC1155"
            | "NATIVE"
            | "UNKNOWN",
        },
        from: String(
          (transfer.from as Record<string, unknown>)?.hash ??
            transfer.from ??
            ""
        ),
        to: String(
          (transfer.to as Record<string, unknown>)?.hash ?? transfer.to ?? ""
        ),
        value: String(
          (transfer.total as Record<string, unknown>)?.value ??
            transfer.value ??
            "0"
        ),
        tokenId: transfer.token_id ? String(transfer.token_id) : undefined,
      };
    }) as import("./blockchain/types").TokenTransfer[],
  })) as import("./blockchain/types").WalletState[];
}

/**
 * Transform raw API output to TokenCard component format
 */
function transformTokenStatesOutput(output: unknown) {
  if (!output) {
    return;
  }

  // Check for legacy error format
  if (typeof output === "object" && "error" in (output as object)) {
    return output as { error: string };
  }

  // Check for AIToolError format
  if (isAIToolError(output)) {
    return {
      error: output.userMessage,
      suggestions: output.suggestions,
      context: output.context,
    };
  }

  const rawOutput = output as Record<
    string,
    {
      type: string;
      metadata?: Record<string, unknown>;
      holders?: Record<string, unknown>[];
      error?: string;
    }
  >;

  const result: Record<
    string,
    import("./blockchain/types").TokenInfo & {
      holders?: import("./blockchain/types").TokenHolder[];
      error?: string;
    }
  > = {};

  for (const [address, data] of Object.entries(rawOutput)) {
    result[address] = {
      address,
      type: data.type as "ERC20" | "ERC721" | "ERC1155" | "UNKNOWN",
      name: String(data.metadata?.name ?? "Unknown"),
      symbol: String(data.metadata?.symbol ?? "?"),
      decimals: data.metadata?.decimals
        ? Number(data.metadata.decimals)
        : undefined,
      totalSupply: data.metadata?.totalSupply
        ? String(data.metadata.totalSupply)
        : undefined,
      holdersCount: data.metadata?.holders_count
        ? String(data.metadata.holders_count)
        : undefined,
      exchangeRate: data.metadata?.exchange_rate
        ? String(data.metadata.exchange_rate)
        : undefined,
      iconUrl: data.metadata?.icon_url
        ? String(data.metadata.icon_url)
        : undefined,
      description: data.metadata?.description
        ? String(data.metadata.description)
        : undefined,
      holders: data.holders?.map((holder) => ({
        address: String(holder.address ?? ""),
        balance: String(holder.balance ?? "0"),
        isContract: Boolean(holder.is_contract),
        name: holder.name ? String(holder.name) : undefined,
      })),
      error: data.error,
    };
  }

  return result;
}

/**
 * Transform raw API output to EventLogs component format
 */
function transformEventLogsOutput(output: unknown) {
  if (!output) {
    return;
  }

  // Check for legacy error format
  if (typeof output === "object" && "error" in (output as object)) {
    return output as { error: string };
  }

  // Check for AIToolError format
  if (isAIToolError(output)) {
    return {
      error: output.userMessage,
      suggestions: output.suggestions,
      context: output.context,
    };
  }

  const rawOutput = output as Record<string, unknown>;

  // Transform the raw API response to our component format
  const logs = (rawOutput.logs as Record<string, unknown>[]) ?? [];

  return {
    logs: logs.map((log) => ({
      address: String(log.address ?? ""),
      topics: (log.topics as string[]) ?? [],
      data: String(log.data ?? "0x"),
      blockNumber: String(log.blockNumber ?? "0"),
      timestamp: String(log.timestamp ?? ""),
      transactionHash: String(log.transactionHash ?? ""),
      logIndex: String(log.logIndex ?? "0"),
    })) as import("./blockchain/types").EventLogEntry[],
    count: rawOutput.count ? Number(rawOutput.count) : logs.length,
    blockRange: (rawOutput.blockRange as { from: string; to: string }) ?? {
      from: "0",
      to: "latest",
    },
    queryParams:
      (rawOutput.queryParams as import("./blockchain/types").EventLogQueryParams) ??
      {},
    message: rawOutput.message ? String(rawOutput.message) : undefined,
    truncated: rawOutput.truncated ? Boolean(rawOutput.truncated) : undefined,
  } as import("./blockchain/types").EventLogsOutput;
}

export const PreviewMessage = memo(
  PurePreviewMessage,
  (prevProps, nextProps) => {
    if (prevProps.isLoading !== nextProps.isLoading) {
      return false;
    }
    if (prevProps.message.id !== nextProps.message.id) {
      return false;
    }
    if (prevProps.requiresScrollPadding !== nextProps.requiresScrollPadding) {
      return false;
    }
    if (!equal(prevProps.message.parts, nextProps.message.parts)) {
      return false;
    }
    if (!equal(prevProps.vote, nextProps.vote)) {
      return false;
    }

    return false;
  }
);

export const ThinkingMessage = () => {
  const role = "assistant";

  return (
    <motion.div
      animate={{ opacity: 1 }}
      className="group/message w-full"
      data-role={role}
      data-testid="message-assistant-loading"
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      initial={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex items-start justify-start gap-3">
        <div className="-mt-1 flex size-8 shrink-0 items-center justify-center rounded-full bg-background ring-1 ring-border">
          <SparklesIcon size={14} />
        </div>

        <div className="flex w-full flex-col gap-2 md:gap-4">
          <div className="p-0 text-muted-foreground text-sm">Thinking...</div>
        </div>
      </div>
    </motion.div>
  );
};
